import React, { useState } from "react";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";
import { Badge, Button, ImagePlus } from "@opendate-app/setlist";
import ImageCropModal from "../../shared/ImageCropModal";
import { fileToDataUrl } from "../../shared/cropUtils";
import { toastError } from "../../shared/toastHelper";
import "./classesShared.css";

export const CLASS_IMAGE_ASPECT_RATIO = 3 / 2;
const CLASS_IMAGE_HINT = "PNG, JPG, or GIF, cropped to 3:2. 1500 × 1000 or larger looks best.";

export default function ClassImageField({ label, imageUrl, fileName, sourceLabel, hint, onImage, onRemove }) {
  const [crop, setCrop] = useState(null);
  const defaultName = "class-image.jpg";

  const startCrop = async (files) => {
    if (!files.length) return;
    try {
      setCrop({ src: await fileToDataUrl(files[0]), name: files[0].name });
    } catch {
      toastError("Could not read that image. Try another file.");
    }
  };

  return (
    <div className="classes-image-field">
      <span className="classes-image-field__label">{label}</span>
      {hint && <span className="classes-image-field__hint">{hint}</span>}
      <Dropzone maxFiles={1} accept="image/*" onDrop={startCrop} noClick={!!imageUrl} noKeyboard={!!imageUrl}>
        {({ getRootProps, getInputProps, open }) => (
          <div {...getRootProps({ className: `classes-image-field__drop${imageUrl ? " classes-image-field__drop--filled" : ""}`, role: imageUrl ? "group" : "button", "aria-label": imageUrl ? label : undefined })}>
            <input {...getInputProps()} aria-label={label} />
            {imageUrl ? (
              <>
                <img src={imageUrl} alt="" className="classes-image-field__thumb" />
                <div className="classes-image-field__meta">
                  <span className="classes-image-field__name">
                    <strong>{fileName || "Current image"}</strong>
                    {sourceLabel && <Badge color="gray" size="sm">{sourceLabel}</Badge>}
                  </span>
                  <span className="classes-image-field__hint">{CLASS_IMAGE_HINT}</span>
                  <div className="classes-image-field__actions">
                    <Button variant="link-color" size="sm" onClick={() => setCrop({ src: imageUrl, name: fileName || defaultName })}>Crop</Button>
                    <Button variant="link-color" size="sm" onClick={open}>Replace</Button>
                    <Button variant="link-destructive" size="sm" onClick={onRemove}>Remove</Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="classes-image-field__empty">
                <span className="classes-image-field__icon">
                  <ImagePlus width={20} height={20} aria-hidden="true" />
                </span>
                <span>
                  <strong className="classes-image-field__link">Upload image</strong>
                  {" "}
                  <span className="classes-image-field__hint">or drag and drop</span>
                  <br />
                  <span className="classes-image-field__hint">{CLASS_IMAGE_HINT}</span>
                </span>
              </div>
            )}
          </div>
        )}
      </Dropzone>
      <ImageCropModal
        open={!!crop}
        onClose={() => setCrop(null)}
        onCropComplete={(file) => {
          onImage(file);
          setCrop(null);
        }}
        imageSrc={crop ? crop.src : null}
        aspectRatio={CLASS_IMAGE_ASPECT_RATIO}
        fileName={crop ? crop.name : defaultName}
      />
    </div>
  );
}

ClassImageField.propTypes = {
  label: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  fileName: PropTypes.string,
  sourceLabel: PropTypes.string,
  hint: PropTypes.string,
  onImage: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

ClassImageField.defaultProps = {
  imageUrl: null,
  fileName: null,
  sourceLabel: null,
  hint: null,
};
